import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ShoppingBag, CheckCircle, Plus, Minus, Search, Bike, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ServerNewOrder = ({
  categories, filtered, cart, selectedCategory, setSelectedCategory,
  searchQuery, setSearchQuery, addToCart, updateQty,
  selectedTable, setSelectedTable, isTakeaway, setIsTakeaway,
  orderNotes, setOrderNotes, myTables, cartTotal, placeOrder, formatPrice,
  isGrabOrder, grabOrderId, setGrabOrderId,
  orderSource, setOrderSource,
  selectedPlatform, platformOrderId, setPlatformOrderId, PLATFORM_META,
}) => {
  const [lowStockItems, setLowStockItems] = useState({});

  // Fetch stock info
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stocks`);
        const stockMap = {};
        for (const item of data.items || []) {
          if (item.stock_count !== null && item.stock_count !== undefined) {
            stockMap[item.id] = item.stock_count;
          }
        }
        setLowStockItems(stockMap);
      } catch { /* ignore */ }
    };
    fetchStocks();
  }, []);

  const platformMeta = selectedPlatform ? (PLATFORM_META?.[selectedPlatform] || { name: selectedPlatform, color: '#888' }) : null;

  return (
    <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
      {/* Cart */}
      <div className="md:w-72 lg:w-80 md:order-2 border-b md:border-b-0 md:border-l border-[#E5DCD3] bg-white flex flex-col" data-testid="server-cart">
        <div className="px-4 py-3 border-b border-[#E5DCD3]">
          <h3 className="font-semibold text-[#2D241D] flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-[#E16833]" />
            Panier ({cart.reduce((s, i) => s + i.quantity, 0)})
          </h3>
        </div>

        <div className="px-4 py-2 border-b border-[#E5DCD3] space-y-2">
          <select value={isTakeaway ? (orderSource || 'takeaway') : selectedTable}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'takeaway') { setIsTakeaway(true); setSelectedTable(''); setOrderSource?.('takeaway'); }
              else if (val === 'click_collect') { setIsTakeaway(true); setSelectedTable(''); setOrderSource?.('click_collect'); }
              else { setIsTakeaway(false); setSelectedTable(val); setOrderSource?.('dine_in'); }
            }}
            className="w-full h-9 rounded-lg border border-[#E5DCD3] px-2 text-sm bg-white text-[#2D241D]"
            data-testid="table-select">
            <option value="">Choisir une table...</option>
            {myTables.map(t => (<option key={t} value={t}>Table {t}</option>))}
            <option value="takeaway">A emporter (TA)</option>
            <option value="click_collect">Click & Collect (CAC)</option>
          </select>
          <Input value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
            placeholder="Notes (allergies, sans piment...)" className="h-8 text-xs rounded-lg border-[#E5DCD3] text-[#2D241D] bg-white"
            data-testid="server-notes" />
          {isGrabOrder && platformMeta && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg flex-shrink-0"
                style={{ backgroundColor: `${platformMeta.color}15` }}>
                <Bike className="w-3.5 h-3.5" style={{ color: platformMeta.color }} />
                <span className="text-[10px] font-bold" style={{ color: platformMeta.color }}>{platformMeta.name.toUpperCase()}</span>
              </div>
              <Input value={platformOrderId || grabOrderId}
                onChange={(e) => { setPlatformOrderId?.(e.target.value); setGrabOrderId(e.target.value); }}
                placeholder={`N° commande ${platformMeta.name} (optionnel)`}
                className="h-8 text-xs rounded-lg text-[#2D241D] bg-white flex-1"
                style={{ borderColor: `${platformMeta.color}40` }}
                data-testid="platform-order-id-input" />
            </div>
          )}
        </div>

        <div className="overflow-y-auto px-4 py-2 space-y-2 max-h-[20vh] md:max-h-[40vh]">
          {cart.length === 0 && (
            <div className="text-center py-4 text-[#6E5E52] text-sm">
              <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Prenez la commande du client
            </div>
          )}
          {cart.map(item => (
            <div key={item.item_id} className="flex items-center gap-2 bg-[#F3EDE5] rounded-lg px-3 py-2" data-testid={`cart-item-${item.item_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#2D241D] truncate">{item.name}</p>
                <p className="text-[10px] text-[#6E5E52]">{item.price} THB</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => updateQty(item.item_id, -1)} className="w-6 h-6 rounded-md bg-white flex items-center justify-center text-[#6E5E52]"
                  data-testid={`cart-minus-${item.item_id}`}><Minus className="w-3 h-3" /></button>
                <span className="w-5 text-center text-xs font-bold text-[#2D241D]">{item.quantity}</span>
                <button onClick={() => updateQty(item.item_id, 1)} className="w-6 h-6 rounded-md bg-[#0A422D] text-white flex items-center justify-center"
                  data-testid={`cart-plus-${item.item_id}`}><Plus className="w-3 h-3" /></button>
              </div>
              <span className="text-xs font-bold text-[#E16833] w-14 text-right">{(item.price * item.quantity).toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E5DCD3] px-4 py-3 space-y-2 bg-white">
          <div className="flex justify-between font-bold text-[#2D241D]">
            <span>Total</span>
            <span className="text-[#E16833]" data-testid="cart-total">{formatPrice(cartTotal)}</span>
          </div>
          <Button onClick={placeOrder} disabled={cart.length === 0 || (!selectedTable && !isTakeaway)}
            className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl h-12 font-semibold text-sm"
            data-testid="place-order-btn">
            <CheckCircle className="w-5 h-5 mr-2" /> Commander & Envoyer
          </Button>
        </div>
      </div>

      {/* Menu */}
      <div className="flex-1 md:order-1 flex flex-col overflow-hidden">
        <div className="px-3 pt-3 pb-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un plat..."
              className="pl-10 rounded-xl border-[#E5DCD3] bg-white h-10 text-sm text-[#2D241D]"
              data-testid="server-search" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            <button onClick={() => setSelectedCategory('all')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedCategory === 'all' ? 'bg-[#0A422D] text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'}`}
              data-testid="cat-all">Tout</button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedCategory === cat.id ? 'bg-[#0A422D] text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'}`}
                data-testid={`cat-${cat.id}`}>
                {cat.name_fr || cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {filtered.map(item => {
              const inCart = cart.find(i => i.item_id === item.id);
              const stockCount = lowStockItems[item.id];
              const isLowStock = stockCount !== undefined && stockCount <= 5 && stockCount > 0;
              const isOutOfStock = stockCount !== undefined && stockCount <= 0;
              return (
                <button key={item.id} onClick={() => !isOutOfStock && addToCart(item)}
                  disabled={isOutOfStock}
                  className={`bg-white rounded-xl p-3 text-left border transition-all active:scale-95 relative ${
                    isOutOfStock ? 'border-red-200 opacity-50 cursor-not-allowed'
                      : inCart ? 'border-[#0A422D] shadow-md'
                        : 'border-[#E5DCD3]'
                  }`}
                  data-testid={`menu-item-${item.id}`}>
                  <div className="w-full h-16 mb-2 rounded-lg bg-[#F3EDE5] flex items-center justify-center overflow-hidden"
                    style={item.image_url ? { backgroundImage: `url(${item.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                    {!item.image_url && <span className="text-sm font-bold text-[#D4A853]">{item.name?.charAt(0)}</span>}
                  </div>
                  <p className="text-xs font-medium text-[#2D241D] leading-tight line-clamp-2">
                    {item.name} {item.spice_level > 0 && <span>{'🌶'.repeat(item.spice_level)}</span>}
                  </p>
                  <p className="text-xs font-bold text-[#E16833] mt-1">{item.price} THB</p>

                  {/* Stock indicators */}
                  {isOutOfStock && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] font-bold"
                      data-testid={`stock-out-${item.id}`}>
                      EPUISE
                    </div>
                  )}
                  {isLowStock && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#F59E0B] text-white text-[9px] font-bold flex items-center gap-0.5"
                      data-testid={`stock-low-${item.id}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> {stockCount}
                    </div>
                  )}

                  {inCart && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#0A422D] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                      {inCart.quantity}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
